import type { ComponentType } from 'react'
import { useEffect, useState } from 'react'
import { Platform } from 'react-native'

import { logger } from '../logging/logger'

/** The props this build asks of the picker, whichever platform draws it. */
export interface NativeDatePickerProps {
  value: Date
  mode: 'date'
  display: 'inline' | 'calendar' | 'spinner'
  minimumDate: Date
  maximumDate: Date
  themeVariant?: 'light' | 'dark'
  onChange: (event: { type: string }, date?: Date) => void
}

export type NativeDatePicker = ComponentType<NativeDatePickerProps>

/**
 * The picker, `null` when the peer is not installed, `undefined` while that
 * is still being decided.
 */
export type NativeDatePickerState = NativeDatePicker | null | undefined

/**
 * Resolved once per session and reused: a host without the module must not
 * retry a failing import every time the step is opened.
 */
let loading: Promise<NativeDatePicker | null> | undefined

/**
 * The platform's own date picker, from the optional
 * `@react-native-community/datetimepicker` peer.
 *
 * Native by design decision — a hand-drawn calendar is not the control the
 * platform trains people on. The peer is optional so installing the SDK does
 * not force a native module on hosts that never take a fiat deposit; the
 * field falls back to a typed box when it is absent.
 *
 * The import is dynamic and inside a `try`, which is what makes the peer
 * genuinely optional: Metro treats a dependency whose import can reject as
 * optional and skips it at resolution rather than failing the bundle.
 */
export function useNativeDatePicker(): NativeDatePickerState {
  const [picker, setPicker] = useState<NativeDatePickerState>(
    // Settled before the first paint off-device, so the box never flips from
    // a picker's to a typed one: the module draws nothing but a console
    // warning anywhere but iOS and Android.
    DRAWS_A_PICKER ? undefined : null,
  )

  useEffect(() => {
    if (!DRAWS_A_PICKER) {
      return
    }
    let isMounted = true
    loading ??= loadNativeDatePicker()
    void loading.then((resolved) => {
      if (isMounted) {
        // Wrapped, because the picker IS a function: handed to the setter
        // bare, React reads it as an updater and calls the component with the
        // previous state as its props — which throws inside the picker on its
        // first prop.
        setPicker(() => resolved)
      }
    })
    return () => {
      isMounted = false
    }
  }, [])

  return picker
}

/**
 * Platforms the module actually draws on. Everywhere else — the web canvas
 * Storybook renders in included — it returns `null` and warns, which would
 * leave the field a box that opens nothing.
 */
const DRAWS_A_PICKER = Platform.OS === 'ios' || Platform.OS === 'android'

/** How each platform draws a calendar: iOS inline, Android as its dialog. */
export const DATE_PICKER_DISPLAY = Platform.OS === 'ios' ? 'inline' : 'calendar'

/**
 * Whether the picker takes room on the screen. iOS draws it inline, so the
 * step must give up the keyboard's slot; Android opens a dialog over
 * everything and needs none.
 */
export const DATE_PICKER_IS_INLINE = Platform.OS === 'ios'

export async function loadNativeDatePicker(): Promise<NativeDatePicker | null> {
  try {
    const module = await import('@react-native-community/datetimepicker')
    const picker: unknown = module.default
    if (typeof picker !== 'function') {
      // Resolved but unusable: a native module the app never linked, or an
      // interop that hands back a namespace. Answering `null` types the date;
      // answering nothing would leave the field loading for the session.
      warnTyped('resolved_without_component')
      return null
    }
    return picker as NativeDatePicker
  } catch (error) {
    warnTyped('import_rejected', error)
    return null
  }
}

/**
 * Why the date ended up typed. Not a failure — the field still collects it —
 * but a host that meant to have the picker has no other way to tell a missing
 * peer from one the build never linked.
 */
function warnTyped(reason: string, error?: unknown) {
  logger.warn('connect-rn:fiat:date_picker_unavailable', {
    reason,
    peer: '@react-native-community/datetimepicker',
    ...(error === undefined
      ? {}
      : { error: error instanceof Error ? error.message : String(error) }),
  })
}
